import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Grid,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Forum as ForumIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  BarChart as BarChartIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// ── API 헬퍼 ──────────────────────────────────────────
const getAdminHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
});

const fetchAdminSessions = async ({ page, limit, search, startDate, endDate }) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append('search', search);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const res = await fetch(`/api/visitor-sessions/admin/sessions?${params}`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error('세션 목록 조회 실패');
  return res.json();
};

const fetchAdminStats = async () => {
  const res = await fetch('/api/visitor-sessions/admin/stats', {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error('통계 조회 실패');
  return res.json();
};

const fetchSessionMessages = async (sessionId) => {
  const res = await fetch(`/api/visitor-sessions/${sessionId}/messages`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error('메시지 조회 실패');
  return res.json();
};

// ── 유틸 ──────────────────────────────────────────────
const formatDateTime = (dt) => {
  if (!dt) return '-';
  try {
    return new Date(dt).toLocaleString('ko-KR', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return dt; }
};

const formatDuration = (sec) => {
  if (sec === null || sec === undefined) return '-';
  if (sec < 60) return `${sec}초`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}분 ${s}초`;
  return `${Math.floor(m / 60)}시간 ${m % 60}분`;
};

const shortId = (id) => (id ? id.slice(0, 14) + '…' : '-');

// ── 통계 카드 ─────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
  <Card sx={{ borderLeft: `4px solid ${color}`, height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color, fontSize: 36 }}>{icon}</Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>{value ?? '-'}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

// ── 메인 컴포넌트 ─────────────────────────────────────
const ChatSessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 필터
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // 대화 상세
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, statsData] = await Promise.all([
        fetchAdminSessions({ page, limit, search, startDate, endDate }),
        fetchAdminStats(),
      ]);
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setStats(statsData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, startDate, endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const openDetail = async (sess) => {
    setDetailSession(sess);
    setDetailOpen(true);
    setMessagesLoading(true);
    setMessages([]);
    try {
      const data = await fetchSessionMessages(sess.session_id);
      setMessages(data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ForumIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>대화 세션 관리</Typography>
            <Typography variant="body2" color="text.secondary">
              사용자가 시작하고 종료한 대화 단위별 조회
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={loadData} title="새로고침"><RefreshIcon /></IconButton>
      </Box>

      {/* 통계 카드 */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard icon={<ForumIcon fontSize="inherit" />} label="총 대화 수" value={stats.total_sessions.toLocaleString()} color="#1976d2" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard icon={<ChatIcon fontSize="inherit" />} label="총 메시지 수" value={stats.total_messages.toLocaleString()} color="#388e3c" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard icon={<PeopleIcon fontSize="inherit" />} label="방문자 수" value={stats.total_visitors.toLocaleString()} color="#f57c00" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard icon={<BarChartIcon fontSize="inherit" />} label="평균 메시지 수" value={stats.avg_messages} color="#7b1fa2" />
          </Grid>
        </Grid>
      )}

      {/* 필터 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="검색 (제목 / 방문자ID)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{ endAdornment: <SearchIcon sx={{ color: 'text.disabled' }} /> }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="시작일" type="date"
                value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="종료일" type="date"
                value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSearch} startIcon={<SearchIcon />}>검색</Button>
                <Button variant="outlined" size="small" onClick={handleReset}>초기화</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 테이블 */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary">
              전체 {total.toLocaleString()}건
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>대화 제목</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">메시지</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>방문자 ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>시작 시각</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>종료 시각</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">소요 시간</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">보기</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      대화 세션이 없습니다
                    </TableCell>
                  </TableRow>
                ) : sessions.map((sess) => (
                  <TableRow key={sess.session_id} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {sess.title}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`${sess.message_count}회`} size="small"
                        color={sess.message_count >= 5 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={sess.visitor_id}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {shortId(sess.visitor_id)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{formatDateTime(sess.started_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {sess.ended_at ? formatDateTime(sess.ended_at) : (
                          <Chip label="진행중" size="small" color="success" variant="outlined" />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="body2">{formatDuration(sess.duration_sec)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="대화 내용 보기">
                        <IconButton size="small" onClick={() => openDetail(sess)} color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 대화 상세 다이얼로그 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ForumIcon color="primary" />
            <Box>
              <Typography fontWeight={700}>{detailSession?.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(detailSession?.started_at)} · {detailSession?.message_count}개 메시지 · 소요 {formatDuration(detailSession?.duration_sec)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 2, minHeight: 300 }}>
          {messagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}><CircularProgress /></Box>
          ) : messages.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ pt: 5 }}>메시지가 없습니다</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map((m) => (
                <Box key={m.id}>
                  {/* 사용자 메시지 */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.400', fontSize: 14 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.3, display: 'block' }}>사용자</Typography>
                      <Paper variant="outlined" sx={{ px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.user}</Typography>
                      </Paper>
                    </Box>
                  </Box>
                  {/* AI 답변 */}
                  <Box sx={{ display: 'flex', gap: 1, pl: 4 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 14 }}>
                      <SmartToyIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.3, display: 'block' }}>AI 답변</Typography>
                      <Paper variant="outlined" sx={{ px: 1.5, py: 1, bgcolor: 'primary.50', borderRadius: 2 }}>
                        <Box
                          sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
                            '& th, & td': { border: '1px solid #ddd', px: 1, py: 0.5, fontSize: '0.8rem' },
                            '& th': { bgcolor: '#f5f5f5', fontWeight: 700 },
                            '& tr:nth-of-type(even)': { bgcolor: '#fafafa' },
                            '& div, & p': { mb: 0.5 },
                          }}
                          dangerouslySetInnerHTML={{ __html: m.assistant }}
                        />
                      </Paper>
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.3, display: 'block' }}>
                        {formatDateTime(m.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatSessionsPage;
